import { type Cart as CartData } from "@cappelendamm/e-commerce-services";

export function calculateOptimisticTotal(
  items: CartData["items"],
): Pick<CartData["total"], "gross" | "net"> {
  return {
    gross: items.reduce((acc, cur) => acc + cur.price.gross, 0),
    net: items.reduce((acc, cur) => acc + cur.price.net, 0),
  };
}

export function calculateOptimisticIncrease(
  cartData: CartData,
  sku: string,
  quantity: number,
) {
  const items: CartData["items"] =
    cartData.items.map((item) => {
      if (item.variant.sku === sku) {
        const newQuantity = item.quantity + quantity;
        const newPrice = {
          ...item.variant.price,
          gross: newQuantity * item.variant.price.gross,
          net: newQuantity * item.variant.price.net,
        };
        return { ...item, quantity: newQuantity, price: newPrice };
      }
      return item;
    }) ?? [];

  return {
    ...cartData,
    items,
    total: { ...cartData.total, ...calculateOptimisticTotal(items) },
  };
}

export function calculateOptimisticDecrease(
  cartData: CartData,
  sku: string,
  quantity: number,
) {
  const items: CartData["items"] =
    cartData.items
      .map((item) => {
        if (item.variant.sku === sku) {
          const newQuantity = item.quantity - quantity;
          const newPrice = {
            ...item.variant.price,
            gross: newQuantity * item.variant.price.gross,
            net: newQuantity * item.variant.price.net,
          };
          return { ...item, quantity: newQuantity, price: newPrice };
        }
        return item;
      })
      .filter((item) => item.quantity > 0) ?? [];
  return {
    ...cartData,
    items,
    total: { ...cartData.total, ...calculateOptimisticTotal(items) },
  };
}

export function calculateOptimisticRemove(cartData: CartData, sku: string) {
  const items: CartData["items"] = cartData.items.filter(
    (item) => item.variant.sku !== sku,
  );
  return {
    ...cartData,
    items,
    total: { ...cartData.total, ...calculateOptimisticTotal(items) },
  };
}

export function calculateOptimisticAdd(
  cartData: CartData,
  sku: string,
  name: string,
  price: number,
) {
  const itemInCart = cartData.items.some((item) => item.variant.sku === sku);
  if (itemInCart) return calculateOptimisticIncrease(cartData, sku, 1);
  const items: CartData["items"] = [
    ...cartData.items,
    {
      name,
      price: {
        gross: price,
        net: price,
        currency: "NOK",
        taxAmount: 0,
      },
      quantity: 1,
      variant: {
        sku,
        attributes: { Innbinding: "" },
        product: {
          id: "",
          path: "loading",
        },
        price: {
          gross: price,
          net: price,
          currency: "NOK",
          taxAmount: 0,
        },
      },
    },
  ];
  return {
    ...cartData,
    items,
    total: { ...cartData.total, ...calculateOptimisticTotal(items) },
  };
}

export function optimisticCartReducer(
  currentCartData: CartData & { isPending: boolean },
  action:
    | {
        type: "increase-quantity" | "decrease-quantity";
        sku: string;
        quantity: number;
      }
    | { type: "remove-item"; sku: string }
    | {
        type: "add-item";
        sku: string;
        name: string;
        price: number;
      },
): CartData & { isPending: boolean } {
  if (action.type === "increase-quantity") {
    return {
      ...calculateOptimisticIncrease(
        currentCartData,
        action.sku,
        action.quantity,
      ),
      isPending: true,
      updatedAt: "optimistic",
    };
  }
  if (action.type === "decrease-quantity")
    return {
      ...calculateOptimisticDecrease(
        currentCartData,
        action.sku,
        action.quantity,
      ),
      isPending: true,
      updatedAt: "optimistic",
    };
  if (action.type === "remove-item")
    return {
      ...calculateOptimisticRemove(currentCartData, action.sku),
      isPending: true,
      updatedAt: "optimistic",
    };
  if (action.type === "add-item")
    return {
      ...calculateOptimisticAdd(
        currentCartData,
        action.sku,
        action.name,
        action.price,
      ),
      isPending: true,
      updatedAt: "optimistic",
    };
  return currentCartData;
}
